import React, { useState, useEffect } from 'react';
import {
  Download,
  Layout,
  Send,
  Church,
  Image as ImageIcon,
  FileText,
  CheckCircle,
  AlertCircle,
  Camera,
  Hammer,
  MapPin,
  Map,
  Loader2,
  Trash2,
  Search,
  X
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot
} from 'firebase/firestore';

// =====================================================================
// CONFIGURAÇÃO FIREBASE — lida das variáveis de ambiente da Vercel
// =====================================================================
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const APP_ID = process.env.REACT_APP_APP_ID || 'relatorio-igrejas-v3';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
// PPTX Generator CDN
const PPTXGEN_CDN = "https://cdn.jsdelivr.net/gh/gitbrent/PptxGenJS@3.12.0/dist/pptxgen.bundle.js";

const ADMIN_USER = 'predialrenascer';
const ADMIN_PASS = 'Sul321x@#';

const App = () => {
  const [view, setView] = useState('survey');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [pptxReady, setPptxReady] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [search, setSearch] = useState('');
  const [exportingId, setExportingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(() => sessionStorage.getItem('isAdmin') === '1');
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({ user: '', pass: '' });
  const [loginError, setLoginError] = useState(false);

  const CATEGORIES = [
    { id: 'fachada', label: 'Fachada da Igreja', icon: <Church size={18} /> },
    { id: 'nave_1', label: 'Nave (Do Altar para as Cadeiras)', icon: <Camera size={18} /> },
    { id: 'nave_2', label: 'Nave (Do Fundo para o Altar)', icon: <Camera size={18} /> },
    { id: 'recepcao', label: 'Recepção', icon: <ImageIcon size={18} /> },
    { id: 'kids', label: 'Ministério Kids', icon: <ImageIcon size={18} /> },
    { id: 'banheiro', label: 'Banheiros', icon: <ImageIcon size={18} /> }
  ];

  const getInitialFormData = () => ({
    churchName: '',
    stateName: '',
    leaderName: '',
    address: '',
    date: new Date().toLocaleDateString('pt-BR'),
    items: CATEGORIES.map(cat => ({
      categoryId: cat.id,
      categoryLabel: cat.label,
      images: [],
      description: ''
    }))
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Autenticação anônima
  useEffect(() => {
    const initAuth = async () => {
      try {
        await signInAnonymously(auth);
      } catch (err) {
        console.error("Auth error:", err);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  // Sincronização em tempo real com Firestore
  useEffect(() => {
    if (!user) return;
    const reportsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'reports');
    const unsubscribe = onSnapshot(reportsRef,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sortedData = data.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setSubmissions(sortedData);
        setLoadingReports(false);
      },
      (error) => {
        console.error("Firestore sync error:", error);
      }
    );
    return () => unsubscribe();
  }, [user]);

  // Carrega biblioteca PPTX
  useEffect(() => {
    const script = document.createElement('script');
    script.src = PPTXGEN_CDN;
    script.async = true;
    script.onload = () => setPptxReady(true);
    script.onerror = () => setPptxReady(true);
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginData.user === ADMIN_USER && loginData.pass === ADMIN_PASS) {
      sessionStorage.setItem('isAdmin', '1');
      setIsAdmin(true);
      setShowLogin(false);
      setView('admin');
      setLoginError(false);
      setLoginData({ user: '', pass: '' });
    } else {
      setLoginError(true);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este relatório permanentemente?')) return;
    try {
      await deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'reports', id));
      showNotification('Relatório excluído.');
    } catch (err) {
      console.error(err);
      showNotification('Falha ao excluir. Tente novamente.', 'error');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('isAdmin');
    setIsAdmin(false);
    setView('survey');
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchAsBase64 = async (url) => {
    if (url.startsWith('data:')) return url;
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const fitImageInBox = (src, box) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ir = img.naturalWidth / img.naturalHeight;
      const br = box.w / box.h;
      const w = ir > br ? box.w : box.h * ir;
      const h = ir > br ? box.w / ir : box.h;
      resolve({ x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h });
    };
    img.src = src;
  });

  const compressImage = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 600;
          const scale = Math.min(1, MAX_WIDTH / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.5));
        };
      };
      reader.readAsDataURL(file);
    });

  const handleImageChange = async (categoryId, e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const item = formData.items.find(i => i.categoryId === categoryId);
    const slots = 5 - (item.images?.length || 0);
    const toProcess = files.slice(0, slots);
    const compressed = await Promise.all(toProcess.map(compressImage));
    const updatedItems = formData.items.map(i =>
      i.categoryId === categoryId
        ? { ...i, images: [...(i.images || []), ...compressed] }
        : i
    );
    setFormData({ ...formData, items: updatedItems });
    e.target.value = '';
  };

  const handleRemoveImage = (categoryId, index) => {
    const updatedItems = formData.items.map(item => {
      if (item.categoryId !== categoryId) return item;
      const images = item.images.filter((_, i) => i !== index);
      return { ...item, images };
    });
    setFormData({ ...formData, items: updatedItems });
  };

  const handleDescriptionChange = (categoryId, text) => {
    const updatedItems = formData.items.map(item =>
      item.categoryId === categoryId ? { ...item, description: text } : item
    );
    setFormData({ ...formData, items: updatedItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      showNotification("Aguardando conexão segura...", "error");
      return;
    }
    const missingPhotos = formData.items.filter(item => !item.images?.length);
    if (missingPhotos.length > 0) {
      showNotification(`Anexo obrigatório: ${missingPhotos[0].categoryLabel}`, 'error');
      return;
    }
    setLoading(true);
    try {
      const reportsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'reports');
      await addDoc(reportsRef, {
        ...formData,
        timestamp: Date.now(),
        submittedAt: new Date().toLocaleString('pt-BR'),
        userId: user.uid
      });
      showNotification('Relatório enviado com sucesso!');
      setFormData(getInitialFormData());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      showNotification("Falha ao salvar. Tente novamente.", "error");
    } finally {
      setLoading(false);
    }
  };

  const getPhotoPositions = (count) => {
    const ax = 0.5, ay = 1.0, aw = 8.2, ah = 5.4, gap = 0.1;
    if (count === 1) return [{ x: ax, y: ay, w: aw, h: ah }];
    const w2 = (aw - gap) / 2;
    if (count === 2) return [
      { x: ax, y: ay, w: w2, h: ah },
      { x: ax + w2 + gap, y: ay, w: w2, h: ah },
    ];
    const rowH = (ah - gap) / 2;
    const row2y = ay + rowH + gap;
    if (count === 3) return [
      { x: ax, y: ay, w: w2, h: rowH },
      { x: ax + w2 + gap, y: ay, w: w2, h: rowH },
      { x: ax, y: row2y, w: aw, h: rowH },
    ];
    if (count === 4) return [
      { x: ax, y: ay, w: w2, h: rowH },
      { x: ax + w2 + gap, y: ay, w: w2, h: rowH },
      { x: ax, y: row2y, w: w2, h: rowH },
      { x: ax + w2 + gap, y: row2y, w: w2, h: rowH },
    ];
    const w3 = (aw - gap * 2) / 3;
    return [
      { x: ax, y: ay, w: w3, h: rowH },
      { x: ax + w3 + gap, y: ay, w: w3, h: rowH },
      { x: ax + (w3 + gap) * 2, y: ay, w: w3, h: rowH },
      { x: ax, y: row2y, w: w2, h: rowH },
      { x: ax + w2 + gap, y: row2y, w: w2, h: rowH },
    ];
  };

  const exportToPPT = async (sub) => {
    if (!window.PptxGenJS) {
      showNotification("Motor de exportação não carregado.", "error");
      return;
    }
    setExportingId(sub.id);
    try {
      // Buscar todas as imagens como base64 (retrocompat: data URL ou URL do Storage)
      const itemsWithData = await Promise.all(
        sub.items.map(async (item) => {
          const raw = item.images?.length > 0
            ? item.images
            : item.image ? [item.image] : [];
          const photos = await Promise.all(raw.map(fetchAsBase64));
          return { ...item, photos };
        })
      );

      const pptx = new window.PptxGenJS();
      pptx.layout = 'LAYOUT_WIDE';

      const slideCapa = pptx.addSlide();
      slideCapa.background = { color: "0F172A" };
      slideCapa.addText("RELATÓRIO FOTOGRÁFICO IGREJAS", {
        x: 0.5, y: 2.2, w: "90%", fontSize: 34, bold: true, color: "FFFFFF", align: "center", fontFace: "Verdana"
      });
      slideCapa.addText(`${sub.churchName.toUpperCase()} / ${sub.stateName.toUpperCase()}`, {
        x: 0.5, y: 3.4, w: "90%", fontSize: 24, color: "38BDF8", align: "center", fontFace: "Arial"
      });
      slideCapa.addText(`Dirigente: ${sub.leaderName}`, {
        x: 0.5, y: 4.2, w: "90%", fontSize: 20, color: "CBD5E1", align: "center", fontFace: "Arial"
      });
      slideCapa.addText(sub.address, {
        x: 0.5, y: 6.2, w: "90%", fontSize: 12, color: "94A3B8", align: "center", italic: true
      });

      for (const item of itemsWithData) {
        const slide = pptx.addSlide();
        slide.addText(item.categoryLabel.toUpperCase(), {
          x: 0.5, y: 0.3, w: "90%", fontSize: 28, color: "0F172A", bold: true, fontFace: "Arial"
        });
        if (item.photos.length > 0) {
          const positions = getPhotoPositions(item.photos.length);
          for (let i = 0; i < item.photos.length; i++) {
            const pos = await fitImageInBox(item.photos[i], positions[i]);
            slide.addImage({ data: item.photos[i], ...pos, rounding: false });
          }
        }
        slide.addShape(pptx.ShapeType.rect, { x: 8.9, y: 1.0, w: 4.1, h: 5.4, fill: { color: "F1F5F9" } });
        slide.addText("NECESSIDADES EMERGENCIAIS:", { x: 9.1, y: 1.2, w: 3.7, fontSize: 14, color: "DC2626", bold: true });
        slide.addText(item.description || "Nenhuma observação relatada.", {
          x: 9.1, y: 1.7, w: 3.7, fontSize: 12, color: "334155", valign: "top"
        });
        slide.addText(`${sub.churchName} - ${sub.stateName}`, { x: 0.5, y: 6.7, fontSize: 10, color: "64748B" });
      }

      await pptx.writeFile({ fileName: `Relatorio_${sub.churchName.replace(/\s/g, '_')}.pptx` });
    } catch (err) {
      console.error(err);
      showNotification("Falha ao exportar. Tente novamente.", "error");
    } finally {
      setExportingId(null);
    }
  };

  if (!user || !pptxReady) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center">
        <Camera className="text-sky-400 mb-6" size={56} />
        <h1 className="font-black text-white text-2xl mb-10 tracking-widest">SISTEMA PREDIAL</h1>
        <div className="space-y-5 w-64">
          <div className="flex items-center gap-3">
            {!user
              ? <Loader2 className="animate-spin text-sky-400 shrink-0" size={20} />
              : <CheckCircle className="text-emerald-400 shrink-0" size={20} />}
            <span className={`font-semibold text-sm ${!user ? 'text-white' : 'text-emerald-400'}`}>
              Conectando ao servidor
            </span>
          </div>
          <div className="flex items-center gap-3">
            {!user
              ? <div className="w-5 h-5 rounded-full border-2 border-slate-600 shrink-0" />
              : !pptxReady
                ? <Loader2 className="animate-spin text-sky-400 shrink-0" size={20} />
                : <CheckCircle className="text-emerald-400 shrink-0" size={20} />}
            <span className={`font-semibold text-sm ${!user ? 'text-slate-500' : !pptxReady ? 'text-white' : 'text-emerald-400'}`}>
              Carregando recursos
            </span>
          </div>
        </div>
        <p className="text-slate-600 text-xs mt-12">Aguarde, preparando o sistema...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 font-sans pb-10">
      <nav className="bg-slate-900 text-white p-4 shadow-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <h1 className="font-black text-xl flex items-center gap-2">
            <Camera className="text-sky-400" /> SISTEMA PREDIAL - FOTOGRÁFICO
          </h1>
          <div className="flex gap-2">
            <button onClick={() => setView('survey')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${view === 'survey' ? 'bg-sky-500 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}>Formulário</button>
            <button
              onClick={() => isAdmin ? setView('admin') : setShowLogin(true)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${view === 'admin' ? 'bg-sky-500 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Gestão {isAdmin && submissions.length > 0 && <span className="ml-1 bg-white text-sky-600 px-1.5 rounded-full text-xs">{submissions.length}</span>}
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 mt-8">
        {view === 'survey' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-200 space-y-6">
              <h2 className="text-2xl font-black text-slate-800 border-b pb-4">Identificação</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase flex items-center gap-1"><Church size={14}/> Nome da Igreja</label>
                  <input required type="text" className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 outline-sky-500" value={formData.churchName} onChange={(e) => setFormData({...formData, churchName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase flex items-center gap-1"><Map size={14}/> Nome da Estadual</label>
                  <input required type="text" className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 outline-sky-500" value={formData.stateName} onChange={(e) => setFormData({...formData, stateName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase flex items-center gap-1"><FileText size={14}/> Nome do Dirigente</label>
                  <input required type="text" className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 outline-sky-500" value={formData.leaderName} onChange={(e) => setFormData({...formData, leaderName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase flex items-center gap-1"><MapPin size={14}/> Endereço Completo</label>
                  <input required type="text" className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 outline-sky-500" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-black text-slate-800 px-2">Fotos e Registros</h2>
              {formData.items.map((item) => (
                <div key={item.categoryId} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
                    <span className="font-bold flex items-center gap-2">{item.categoryLabel}</span>
                    {item.images?.length > 0 && (
                      <span className="flex items-center gap-1.5 text-sky-400 text-sm font-bold">
                        <CheckCircle size={18} /> {item.images.length}/5
                      </span>
                    )}
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                      {(item.images || []).map((img, idx) => (
                        <div key={idx} className="relative aspect-video rounded-xl overflow-hidden group">
                          <img src={img} className="w-full h-full object-cover" alt={`Foto ${idx + 1}`} />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(item.categoryId, idx)}
                            className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      {(item.images || []).length < 5 && (
                        <label className="aspect-video rounded-xl border-2 border-dashed border-slate-300 bg-slate-100 hover:bg-slate-200 flex flex-col items-center justify-center cursor-pointer transition-colors">
                          <Camera size={22} className="text-slate-400 mb-1" />
                          <span className="text-xs font-bold text-slate-400">Adicionar</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleImageChange(item.categoryId, e)}
                          />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 font-medium">{(item.images || []).length}/5 fotos</p>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-red-600 flex items-center gap-2"><Hammer size={16}/> Necessidades Emergenciais</label>
                      <textarea rows="4" className="w-full bg-slate-50 p-4 rounded-2xl border border-slate-200 resize-none outline-red-400" placeholder="Ex: Reforma necessária no telhado, troca de fiação..." value={item.description} onChange={(e) => handleDescriptionChange(item.categoryId, e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button disabled={loading} className="w-full bg-slate-900 text-white py-6 rounded-3xl font-black text-2xl shadow-2xl hover:bg-sky-600 transition-all flex justify-center items-center gap-4 disabled:opacity-50">
              {loading ? <Loader2 className="animate-spin" size={28} /> : <><Send size={28}/> FINALIZAR RELATÓRIO</>}
            </button>

            <div className="text-center text-slate-400 text-sm font-medium">
              Desenvolvido por <a href="https://agenciajs.com" target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">Agência JS</a> em parceria com  Bp. Gabriel Rocha (Florianópolis/SC)
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-black text-slate-800">Histórico de Relatórios</h2>
              <button onClick={handleLogout} className="text-sm font-bold text-slate-400 hover:text-red-500 transition-colors px-3 py-2 rounded-lg hover:bg-red-50">
                Sair
              </button>
            </div>
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Buscar por igreja, dirigente ou estadual..."
                className="w-full bg-white pl-11 pr-4 py-4 rounded-2xl border border-slate-200 outline-sky-500 font-medium shadow-sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            {loadingReports ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white p-6 rounded-3xl border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 animate-pulse">
                    <div className="space-y-3 flex-1">
                      <div className="h-5 bg-slate-200 rounded-lg w-2/3" />
                      <div className="h-4 bg-slate-100 rounded-lg w-1/2" />
                    </div>
                    <div className="h-14 w-44 bg-slate-200 rounded-2xl" />
                  </div>
                ))}
              </div>
            ) : submissions.length === 0 ? (
              <div className="bg-white p-20 rounded-3xl border border-dashed border-slate-300 text-center text-slate-400">
                Nenhum relatório recebido até o momento.
              </div>
            ) : (() => {
              const term = search.toLowerCase().trim();
              const filtered = term
                ? submissions.filter(s =>
                    s.churchName?.toLowerCase().includes(term) ||
                    s.leaderName?.toLowerCase().includes(term) ||
                    s.stateName?.toLowerCase().includes(term)
                  )
                : submissions;
              return filtered.length === 0 ? (
                <div className="bg-white p-16 rounded-3xl border border-dashed border-slate-300 text-center text-slate-400">
                  Nenhum resultado para "<span className="font-bold">{search}</span>".
                </div>
              ) : filtered.map(sub => (
                <div key={sub.id} className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4 hover:border-sky-300 transition-colors">
                  <div>
                    <h3 className="text-xl font-black text-slate-800">{sub.churchName} / {sub.stateName}</h3>
                    <p className="text-slate-500 font-medium text-sm">Dirigente: {sub.leaderName} • {sub.submittedAt}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => exportToPPT(sub)}
                      disabled={exportingId === sub.id}
                      className="bg-sky-500 text-white px-8 py-4 rounded-2xl font-black flex items-center gap-2 hover:bg-sky-600 shadow-lg shadow-sky-100 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-wait"
                    >
                      {exportingId === sub.id
                        ? <><Loader2 size={20} className="animate-spin" /> GERANDO...</>
                        : <><Download size={20} /> BAIXAR PPTX</>}
                    </button>
                    <button onClick={() => handleDelete(sub.id)} className="p-4 rounded-2xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95" title="Excluir relatório">
                      <Trash2 size={22} />
                    </button>
                  </div>
                </div>
              ))
            })()}
          </div>
        )}
      </main>

      {notification && (
        <div className={`fixed bottom-10 right-10 p-6 rounded-2xl shadow-2xl border-2 flex items-center gap-4 bg-white z-[100] ${notification.type === 'success' ? 'border-emerald-500 text-emerald-800' : 'border-red-500 text-red-800'}`}>
          {notification.type === 'success' ? <CheckCircle size={24}/> : <AlertCircle size={24}/>}
          <span className="font-black uppercase tracking-wider">{notification.message}</span>
        </div>
      )}

      {showLogin && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200]" onClick={() => { setShowLogin(false); setLoginError(false); setLoginData({ user: '', pass: '' }); }}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-slate-900 px-8 py-6">
              <h2 className="text-white font-black text-xl">Acesso Restrito</h2>
              <p className="text-slate-400 text-sm mt-1">Área de gestão de relatórios</p>
            </div>
            <form onSubmit={handleLogin} className="p-8 space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase">Usuário</label>
                <input
                  required
                  autoFocus
                  type="text"
                  autoComplete="username"
                  className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 outline-sky-500 font-medium"
                  value={loginData.user}
                  onChange={e => { setLoginData({ ...loginData, user: e.target.value }); setLoginError(false); }}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase">Senha</label>
                <input
                  required
                  type="password"
                  autoComplete="current-password"
                  className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 outline-sky-500 font-medium"
                  value={loginData.pass}
                  onChange={e => { setLoginData({ ...loginData, pass: e.target.value }); setLoginError(false); }}
                />
              </div>
              {loginError && (
                <div className="flex items-center gap-2 text-red-600 text-sm font-bold">
                  <AlertCircle size={16} /> Usuário ou senha incorretos.
                </div>
              )}
              <button type="submit" className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black hover:bg-sky-600 transition-all mt-2">
                Entrar
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
